import React, { useEffect, useState } from 'react';
import IconClose from '../../assets/icons/IconClose';
import {
  TextBold,
  TextMedium,
  TextRegular,
} from '../../styles/GlobalStyledComponents';
import { IconCloseWrapper } from '../ClickToCallModal/ClickToCallModalStyledComponents';
import {
  CardPaymentPlanHeader,
  DetailTextWrapper,
  ModalContainer,
  ModalHeader,
  TableListContainer,
  TablePaymentPlan,
  TableCell,
  TableContainer,
  TableHeader,
  TableRow,
  ContainerButton,
  ColumDataModalPlan,
  CardDataHeader,
  PartialPayDetailInformation,
  TableCellWrapper,
} from '../DetailPaymentPlan/DetailPaymentPlanStyledComponents';
import { colors } from '../../styles/Colors';
import Button from '../Button/Button';
import { pricesHelper } from '../../utils/paymentsHelper';
import {
  PaymentPlanData,
  PaymentPlanStatus,
  PaymentPlanStatusText,
  SimulationPaymentPlan,
} from '../../interfaces/PaymentPlan';
import {
  addMonthsToDate,
  dateFormatStandard,
  getNewDate,
  getNextDateWithMonthDay,
} from '../../utils/dateTimeHelper';
import IconInformation from '../../assets/icons/IconInformation';
import { DetailPartialPayment } from '../DetailPartialPayment/DetailPartialPayment';
import GAnalyticsService from '../../services/GAnalytics/ganalytics.service';

interface DetailPaymentPlanProps {
  close: () => void;
  simulationDetailData?: SimulationPaymentPlan;
  paymentPlan?: PaymentPlanData[];
}

export const DetailPaymentPlan: React.FC<DetailPaymentPlanProps> = ({
  close,
  simulationDetailData,
  paymentPlan,
}) => {
  const [simulationMode, setSimulationMode] = useState<boolean>(false);
  const [initialDate, setInitialDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [quota, setQuota] = useState<string>('');
  const [modalOpened, setModalOpened] = useState(false);
  const [rowIndex, setRowIndex] = useState<number>(0);
  const todayDayOfMonth = new Date().getDate();

  useEffect(() => {
    if (simulationDetailData && !simulationMode) {
      simulationInitialData(simulationDetailData);
    } else {
      setSimulationMode(false);
      if (paymentPlan) {
        paymentPlanInitialData(paymentPlan);
      }
    }
    GAnalyticsService.sendShowPaymentPlanDetails();
  }, []);

  const simulationInitialData = (simulation: SimulationPaymentPlan) => {
    setSimulationMode(true);
    const startDate = new Date();
    setInitialDate(dateFormatStandard(startDate));
    let endDate =
      simulation.month_day === todayDayOfMonth
        ? addMonthsToDate(startDate, simulation.term - 1)
        : addMonthsToDate(
            getNextDateWithMonthDay(simulation.month_day),
            simulation.term - 1
          );
    if (
      endDate.getDate() !== simulation.month_day &&
      simulation.month_day >= 29
    ) {
      // Si el último día del mes no coincide con el del mes actual, se ajusta al último día del mes correcto
      endDate = getNewDate(endDate, simulation.month_day, simulation.term - 1);
    }
    setEndDate(dateFormatStandard(endDate));
    const quotaValue = pricesHelper(simulation.installment);
    setQuota(quotaValue);
  };

  const paymentPlanInitialData = (paymentPlan: PaymentPlanData[]) => {
    if (paymentPlan.length > 0) {
      setInitialDate(dateFormatStandard(new Date(paymentPlan[0].start_date)));
      setEndDate(
        dateFormatStandard(
          new Date(paymentPlan[paymentPlan.length - 1].start_date)
        )
      );
      setQuota(pricesHelper(paymentPlan[0].amount));
    } else {
      close();
    }
  };

  if (!simulationMode && !paymentPlan) {
    return null; // Devuelve null si no hay datos para renderizar
  }

  const showTableRows = () => {
    switch (simulationMode) {
      case true:
        if (simulationDetailData) {
          let rowNumber = 0;
          const rows = [];
          let monthDay = simulationDetailData.month_day;
          while (rowNumber < simulationDetailData.term) {
            //El día actual:
            // const todayDayOfMonth = new Date().getDate();
            // Fecha que se va a pintar por pantalla en la simulación:
            let date =
              rowNumber === 0
                ? new Date()
                : addMonthsToDate(getNextDateWithMonthDay(monthDay), rowNumber);

            if (
              rowNumber > 0 &&
              date.getDate() !== monthDay &&
              monthDay >= 29
            ) {
              // Si el último día del mes no coincide con el del mes actual, se ajusta al último día del mes correcto
              date = getNewDate(date, monthDay, rowNumber);
            }
            rowNumber++;
            rows.push(
              <TableRow key={rowNumber} isGray={rowNumber % 2 === 0}>
                <TableCell size="short" color={colors.red6}>
                  <TextMedium
                    fontSize={14}
                    fontWeight={400}
                    lineHeight={20}
                    color={colors.red6}
                  >
                    {rowNumber}
                  </TextMedium>
                </TableCell>
                <TableCell size="medium">
                  <TextMedium
                    fontSize={14}
                    fontWeight={400}
                    lineHeight={20}
                    color={'gray6'}
                  >
                    {dateFormatStandard(date)}
                  </TextMedium>
                </TableCell>
                <TableCell size="medium">
                  <TextMedium
                    fontSize={14}
                    fontWeight={400}
                    lineHeight={20}
                    color={'gray6'}
                  >
                    {quota} €
                  </TextMedium>
                </TableCell>
              </TableRow>
            );
          }
          return rows;
        }
        break;
      case false:
        if (paymentPlan) {
          return paymentPlan.map((row, index) => {
            const date = dateFormatStandard(new Date(row.start_date));
            return (
              <TableRow key={index} isGray={index % 2 === 0}>
                <TableCell size="short" color={colors.red6}>
                  <TextMedium
                    fontSize={14}
                    fontWeight={400}
                    lineHeight={20}
                    color={colors.red6}
                  >
                    {index + 1}
                  </TextMedium>
                </TableCell>
                <TableCell size="medium">
                  <TextMedium
                    fontSize={14}
                    fontWeight={400}
                    lineHeight={20}
                    color={'gray6'}
                  >
                    {date}
                  </TextMedium>
                </TableCell>
                <TableCell size="medium">
                  <TextMedium
                    fontSize={14}
                    fontWeight={400}
                    lineHeight={20}
                    color={'gray6'}
                  >
                    {row.amount ? pricesHelper(row.amount) : ''} €
                  </TextMedium>
                </TableCell>
                <TableCell size="long">
                  <TableCellWrapper id="debt-detail-table-cell-wrapper">
                    <TextMedium
                      fontSize={14}
                      fontWeight={400}
                      lineHeight={20}
                      color={
                        row.state === PaymentPlanStatus.PEN ? 'red4' : 'gray6'
                      }
                    >
                      {PaymentPlanStatusText[row.state]}
                    </TextMedium>
                    {row.state === PaymentPlanStatus.PEN &&
                      row.collected > 0 &&
                      row.collected < row.amount && (
                        <PartialPayDetailInformation
                          id="debt-detail-partial-pay"
                          onClick={() => {
                            setRowIndex(index);
                            setModalOpened(true);
                          }}
                        >
                          <IconInformation width="15px" height="15px" />
                        </PartialPayDetailInformation>
                      )}
                  </TableCellWrapper>
                </TableCell>
              </TableRow>
            );
          });
        }
        break;
      default:
        const rowsExample = [
          { fecha: '01/04/2025', cuota: '43,12 ', estado: 'Pagada' },
          { fecha: '01/05/2025', cuota: '43,12 ', estado: 'Pagada' },
          { fecha: '01/06/2025', cuota: '43,12 ', estado: 'Pagada' },
          { fecha: '01/07/2025', cuota: '43,12 ', estado: 'Pagada' },
          { fecha: '01/08/2025', cuota: '43,12 ', estado: 'Pagada' },
          { fecha: '01/05/2025', cuota: '43,12 ', estado: 'Pendiente' },
          { fecha: '01/06/2025', cuota: '43,12 ', estado: 'En curso' },
          { fecha: '01/07/2025', cuota: '43,12 ', estado: '-' },
          { fecha: '01/08/2025', cuota: '43,12 ', estado: '-' },
        ];
        return rowsExample.map((row, index) => (
          <TableRow key={index} isGray={index % 2 === 0}>
            <TableCell size="short" color={colors.red6}>
              <TextMedium
                fontSize={14}
                fontWeight={400}
                lineHeight={20}
                color={colors.red6}
              >
                {index + 1}
              </TextMedium>
            </TableCell>
            <TableCell size="medium">
              <TextMedium
                fontSize={14}
                fontWeight={400}
                lineHeight={20}
                color={'gray6'}
              >
                {row.fecha}
              </TextMedium>
            </TableCell>
            <TableCell size="medium">
              <TextMedium
                fontSize={14}
                fontWeight={400}
                lineHeight={20}
                color={'gray6'}
              >
                {row.cuota} €
              </TextMedium>
            </TableCell>
          </TableRow>
        ));
    }
  };

  return (
    <>
      {!modalOpened && (
        <ModalContainer id="debt-detail-modal">
          <ModalHeader id="debt-detail-header">
            <TextBold fontSize={24} lineHeight={32} id="debt-detail-title">
              Tu plan de pagos
            </TextBold>
            <IconCloseWrapper
              onClick={() => close()}
              id="debt-detail-close-icon"
            >
              <IconClose />
            </IconCloseWrapper>
          </ModalHeader>
          <DetailTextWrapper id="debt-detail-text-wrapper">
            <CardPaymentPlanHeader id="debt-detail-card-payment-plan-header">
              <CardDataHeader id="debt-detail-card-data-header">
                <ColumDataModalPlan id="debt-detail-column-data">
                  <TextMedium
                    fontSize={14}
                    lineHeight={16}
                    fontWeight={500}
                    color={'gray0'}
                  >
                    Número de cuotas
                  </TextMedium>
                  <TextMedium
                    fontSize={14}
                    lineHeight={16}
                    fontWeight={800}
                    color={'gray0'}
                  >
                    {simulationMode
                      ? simulationDetailData?.term
                      : paymentPlan?.length}
                  </TextMedium>
                </ColumDataModalPlan>
                <ColumDataModalPlan id="debt-detail-column-data">
                  <TextMedium
                    fontSize={14}
                    lineHeight={16}
                    fontWeight={500}
                    color={'gray0'}
                  >
                    Fecha de inicio
                  </TextMedium>
                  <TextMedium
                    fontSize={14}
                    lineHeight={16}
                    fontWeight={800}
                    color={'gray0'}
                  >
                    {initialDate}
                  </TextMedium>
                </ColumDataModalPlan>
                <ColumDataModalPlan id="debt-detail-column-data">
                  <TextMedium
                    fontSize={14}
                    lineHeight={16}
                    fontWeight={500}
                    color={'gray0'}
                  >
                    Fecha de fin
                  </TextMedium>
                  <TextMedium
                    fontSize={14}
                    lineHeight={16}
                    fontWeight={800}
                    color={'gray0'}
                  >
                    {endDate}
                  </TextMedium>
                </ColumDataModalPlan>
                <ColumDataModalPlan id="debt-detail-column-data">
                  <TextMedium
                    fontSize={14}
                    lineHeight={16}
                    fontWeight={500}
                    color={'gray0'}
                  >
                    Cuota mensual
                  </TextMedium>
                  <TextMedium
                    fontSize={14}
                    lineHeight={16}
                    fontWeight={800}
                    color={'gray0'}
                  >
                    {quota} €
                  </TextMedium>
                </ColumDataModalPlan>
              </CardDataHeader>
            </CardPaymentPlanHeader>
          </DetailTextWrapper>

          <TableListContainer id="debt-detail-table-list-container">
            <TableContainer id="debt-detail-table-container">
              <TablePaymentPlan id="debt-detail-table-payment-plan">
                <thead>
                  <tr>
                    <TableHeader size="short">
                      <TextMedium
                        fontSize={12}
                        fontWeight={500}
                        lineHeight={16}
                        color={'gray0'}
                      >
                        Núm. cuota
                      </TextMedium>
                    </TableHeader>
                    <TableHeader size="medium">
                      <TextMedium
                        fontSize={12}
                        fontWeight={500}
                        lineHeight={16}
                        color={'gray0'}
                      >
                        Fecha
                      </TextMedium>
                    </TableHeader>
                    <TableHeader size="medium">
                      <TextMedium
                        fontSize={12}
                        fontWeight={500}
                        lineHeight={16}
                        color={'gray0'}
                      >
                        Cuota
                      </TextMedium>
                    </TableHeader>
                    {!simulationMode && paymentPlan && (
                      <TableHeader size="long">
                        <TextMedium
                          fontSize={12}
                          fontWeight={500}
                          lineHeight={16}
                          color={'gray0'}
                        >
                          Estado
                        </TextMedium>
                      </TableHeader>
                    )}
                  </tr>
                </thead>
                <tbody>{showTableRows()}</tbody>
              </TablePaymentPlan>
            </TableContainer>
          </TableListContainer>
          {simulationMode && (
            <ContainerButton>
              <Button
                borderColor={colors.gray6}
                backgroundColor={colors.white}
                color={colors.gray0}
                fontWeight={500}
                border={`2px solid ${colors.gray6}`}
                click={() => close()}
                id="debt-detail-close-button"
              >
                <TextRegular fontSize={16} lineHeight={22} color={colors.gray0}>
                  Cerrar
                </TextRegular>
              </Button>
            </ContainerButton>
          )}
        </ModalContainer>
      )}
      {modalOpened && paymentPlan && (
        <DetailPartialPayment
          close={() => setModalOpened(false)}
          isOpen={modalOpened}
          payment={paymentPlan[rowIndex]}
        />
      )}
    </>
  );
};
